import React, { useState, useCallback } from "react";
import { useGetPdfDocumentsQuery, useDeletePdfDocumentMutation, type PdfExtractionDocument } from "@/redux/api/documents.rtkq";
import { toast } from "sonner";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useAuth } from "@/hooks/useAuth";
import { useGetAccountByIdQuery } from "@/redux/api/accounts.rtkq";
import PdfExtractor from "@/components/experiments/PdfExtractor";
import PdfDocumentsTable from "@/components/documents/PdfDocumentsTable";
import PdfDocumentViewer from "@/components/documents/PdfDocumentViewer";
import FeaturePreview from "@/components/FeaturePreview";
import { canUseApp } from "@/utils/appAccess";

const OCR_APP_ID = "scan-documents-extraction";

const DocumentsPage: React.FC = () => {
  const { user } = useAuthContext();
  const auth = useAuth();
  const accountId = auth.getAccountId();

  const { data: account, isSuccess, isLoading } = useGetAccountByIdQuery(
    { accountId: accountId! },
    { skip: !accountId }
  );

  // PDF Documents queries and mutations
  const {
    data: pdfDocuments = [],
    isLoading: pdfLoading,
    error: pdfError,
    refetch: loadPdfDocuments,
  } = useGetPdfDocumentsQuery({ userId: user?.uid || "" }, { skip: !user?.uid });
  const [deletePdfDocument] = useDeletePdfDocumentMutation();

  // PDF Viewer modal state
  const [selectedPdfDocument, setSelectedPdfDocument] = useState<PdfExtractionDocument | null>(null);
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);

  // PDF Document Handlers
  const handleEditPdf = useCallback((doc: PdfExtractionDocument) => {
    setSelectedPdfDocument(doc);
    setIsPdfViewerOpen(true);
  }, []);

  const handleDeletePdf = useCallback(
    async (doc: PdfExtractionDocument) => {
      if (!user?.uid) {
        toast.error("עליך להתחבר תחילה");
        return;
      }

      try {
        await deletePdfDocument({
          userId: user.uid,
          docId: doc.docId,
          storagePath: doc.originalPath,
        }).unwrap();
        toast.success("המסמך נמחק בהצלחה");
        loadPdfDocuments();
      } catch {
        toast.error("שגיאה במחיקת המסמך");
      }
    },
    [user?.uid, deletePdfDocument, loadPdfDocuments]
  );

  // Show loading state
  if (!accountId || isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">טוען...</div>
      </div>
    );
  }

  if (isSuccess && !canUseApp(account, OCR_APP_ID)) {
    return <FeaturePreview appId={OCR_APP_ID} />;
  }

  return (
    <section className="mx-auto space-y-6 bg-indigo-50 p-4 md:p-16" aria-label="חילוץ טקסט מ-PDF">
      {/* PDF Extractor */}
      <PdfExtractor />

      {/* PDF Extractions Table */}
      <PdfDocumentsTable
        documents={pdfDocuments}
        isLoading={pdfLoading}
        error={pdfError}
        onRefresh={loadPdfDocuments}
        onEdit={handleEditPdf}
        onDelete={handleDeletePdf}
      />

      {/* PDF Document Viewer Modal */}
      {selectedPdfDocument && (
        <PdfDocumentViewer
          document={selectedPdfDocument}
          isOpen={isPdfViewerOpen}
          onOpenChange={(open) => {
            setIsPdfViewerOpen(open);
            if (!open) setSelectedPdfDocument(null);
          }}
        />
      )}
    </section>
  );
};

export default DocumentsPage;
